/**
 * Host-side mapping from an org-relative page path (everything after
 * `/orgs/:slug`) to its Configure section. Pages migrate to their new URLs
 * across several PRs; at any given moment this table may point at either the
 * old or the new path. Keeping it accurate is how the nav stays honest
 * mid-migration. Every PR that moves a page updates this table in the same
 * commit.
 *
 * Do NOT widen the plugin `group` union: the host maps (group, path) ->
 * section so third-party plugins still bucket sensibly instead of vanishing.
 */

/**
 * Display groups of the Configure nav, in render order. Connect items have
 * moved to the dedicated Connect takeover surface; entries in
 * {@link CONFIGURE_SECTION_MAP} with `group: 'Connect'` are kept so
 * `resolveConfigureSection` still classifies those paths for the command
 * palette and the Connect shell's nav builder.
 */
export const CONFIGURE_GROUP_ORDER = [
  'Build',
  'People',
  'Usage and Costs',
  /** Empty label: TakeoverNavGroups renders the items without a heading. */
  '',
  'Organization',
  /**
   * Settings that belong to the company that owns this organization, not to
   * the organization itself. Kept as its own group so a control whose blast
   * radius is every organization the company owns is never read as an
   * organization setting.
   */
  'Company',
] as const;

/**
 * Group labels that can appear in a {@link ConfigureSectionMapping}. Includes
 * `'Connect'` even though it no longer renders inside Configure — the label
 * is still used by `resolveConfigureSection` for path classification and by
 * the Connect shell's nav builder.
 */
export type ConfigureGroupLabel = (typeof CONFIGURE_GROUP_ORDER)[number] | 'Connect';

export interface ConfigureSectionMapping {
  /** Target Configure group. Empty string = unlabeled single row. */
  group: ConfigureGroupLabel;
  /** Optional frontend-only label override (e.g. Blueprints -> Environments). */
  label?: string;
  /** Optional description override applied when the item renders in a takeover nav. */
  description?: string;
}

/**
 * Pages that stay routable but have no Configure nav row. Credentials
 * pages other than the entry point fold into the single Credentials row;
 * Agent Types is gone from the UI; Personas fold into the Agents hub at
 * `/configure/build/agents`; Plugins fold into Integrations.
 */
export const HIDDEN_CONFIGURE_PATHS: ReadonlySet<string> = new Set([
  '/configure/connect/credentials/personal/secrets',
  '/configure/connect/credentials/personal/api-keys',
  '/configure/connect/credentials/organization/secrets',
  '/configure/connect/credentials/organization/model-providers',
  '/configure/connect/credentials/team',
  '/configure/connect/sharing/organization',
  '/configure/connect/mcp/setup',
  '/configure/connect/mcp/servers',
  // Legacy paths kept so mid-migration nav items still hide correctly.
  '/security/secrets',
  '/security/api-keys',
  '/org/secrets',
  '/org/model-providers',
  '/org/plugins',
  '/agent-types',
  // Personas + Agent Models live under the Agents hub; no separate nav rows.
  '/configure/build/agents/personas',
  '/configure/build/agents/models',
  // General + Service accounts live under the Organization hub.
  '/configure/organization/general',
  '/configure/organization/service-accounts',
  // Company → Security's old path. It is still routable — the host redirects
  // it to `/configure/company/security` — but it must never produce a second
  // nav row, which is what an unmapped path would do via the Organization
  // fallback if a stale plugin build still contributed it.
  '/configure/organization/enterprise-authorization',
]);

/**
 * Mapping table: org-relative path -> Configure section. Every entry
 * points at the page's CURRENT path in this PR.
 */
export const CONFIGURE_SECTION_MAP: Readonly<Record<string, ConfigureSectionMapping>> = {
  // Build
  '/configure/build/agents': { group: 'Build', label: 'Agents' },
  '/configure/build/environments': { group: 'Build', label: 'Environments' },
  // Connect
  '/configure/connect/integrations': { group: 'Connect', label: 'Integrations' },
  '/configure/connect/mcp': { group: 'Connect', label: 'MCP' },
  '/configure/connect/ai-gateway': { group: 'Connect', label: 'AI Gateway' },
  '/configure/connect/credentials/personal/model-providers': {
    group: 'Connect',
    label: 'Credentials',
    description:
      'Model providers, API keys, and secrets across personal, team, and organization scopes.',
  },
  '/configure/connect/sharing/personal': { group: 'Connect', label: 'Sharing' },
  '/configure/connect/tailscale': { group: 'Connect', label: 'Tailscale' },
  // People
  '/configure/people/roles': { group: 'People' },
  '/configure/people/teams': { group: 'People' },
  '/configure/people/members': { group: 'People' },
  // Usage and Costs
  '/configure/usage/plan': { group: 'Usage and Costs', label: 'Plan' },
  '/configure/usage/sandbox': { group: 'Usage and Costs', label: 'Sandbox Usage' },
  '/configure/usage/model-costs': { group: 'Usage and Costs', label: 'Model Costs' },
  '/configure/usage/sharing': { group: 'Usage and Costs', label: 'Sharing' },
  // Unlabeled single row
  '/configure/network-policies': { group: '', label: 'Network Policies' },
  // Organization — single hub row; General + Service accounts are rail sections.
  '/configure/organization': { group: 'Organization', label: 'Organization' },
  // Company — the level above the organization. Both rows are company-owned
  // and both now live under `/configure/company/`; Security is contributed by
  // the OIDC plugin in its `organization` group (the plugin union is
  // deliberately not widened), so the mapping is still what decides the
  // section for it.
  '/configure/company': { group: 'Company', label: 'Overview' },
  '/configure/company/security': {
    group: 'Company',
    label: 'Security',
    description:
      'Your company’s identity provider connection, and the applications it may authorize.',
  },
};

/**
 * Strip `/orgs/:slug` so plugin and host paths can be bucketed consistently.
 * Returns a leading-slash path (e.g. `/org/settings`).
 */
export function orgRelativePath(to: string, slug: string): string {
  const prefix = `/orgs/${slug}`;
  if (to === prefix) return '/';
  if (to.startsWith(prefix + '/')) {
    return to.slice(prefix.length);
  }
  return to.startsWith('/') ? to : `/${to}`;
}

/**
 * Fallback so third-party / unmapped plugin pages never vanish from the nav.
 * Buckets by the drill group the item arrived in.
 */
export function fallbackGroupForDrillLabel(drillLabel: string): ConfigureGroupLabel {
  switch (drillLabel) {
    case 'Security':
    case 'Integrations':
      return 'Connect';
    case 'Organization':
      return 'Organization';
    default:
      return 'Organization';
  }
}

/**
 * Disambiguating labels for pages the sidebar folds away. Used by the
 * command palette so searching "secrets" / "model providers" can still land
 * on personal vs. team vs. org pages without colliding with the single
 * Credentials nav row. Hub entry points that stay visible as "Credentials"
 * also get a companion search label here (Your model providers).
 */
export const CONFIGURE_SEARCH_LABELS: Readonly<Record<string, string>> = {
  '/configure/connect/credentials/personal/model-providers': 'Your model providers',
  '/configure/connect/credentials/personal/secrets': 'Your secrets',
  '/configure/connect/credentials/personal/api-keys': 'Your API keys',
  '/configure/connect/credentials/team/secrets': 'Team secrets',
  '/configure/connect/credentials/organization/secrets': 'Organization secrets',
  '/configure/connect/credentials/organization/model-providers': 'Organization model providers',
  '/configure/connect/sharing/organization': 'Organization sharing',
  '/configure/organization/general': 'General settings',
  '/configure/organization/service-accounts': 'Service accounts',
};

/**
 * Entry-point paths whose nav item should link to a shorter hub root so that
 * `getActiveDrillItemPath` prefix-matching keeps the sidebar item highlighted
 * across all sub-paths (e.g. personal ↔ organization scope toggles).
 */
export const CONFIGURE_HUB_ROOTS: Readonly<Record<string, string>> = {
  '/configure/connect/credentials/personal/model-providers': '/configure/connect/credentials',
  '/configure/connect/sharing/personal': '/configure/connect/sharing',
};

export interface ResolveConfigureSectionOptions {
  /**
   * When true, hidden pages (folded Credentials sections, per-provider
   * integrations, Organization rail sections) resolve to a group instead
   * of returning `null`. The command palette uses this so those pages stay
   * searchable; the sidebar leaves it false.
   */
  includeHidden?: boolean;
}

/**
 * Resolve a page's Configure section. Returns `null` when the path is
 * hidden (routable, no nav row) unless `includeHidden` is set. Unmapped
 * paths fall back via the drill group label the caller supplies.
 */
export function resolveConfigureSection(
  relativePath: string,
  drillLabel: string,
  options: ResolveConfigureSectionOptions = {},
): ConfigureSectionMapping | null {
  const { includeHidden = false } = options;

  // Exact match first; then the longest matching prefix for detail pages
  // (e.g. `/org/teams/t_1` inherits `/org/teams`).
  const exact = CONFIGURE_SECTION_MAP[relativePath];
  if (exact) return exact;

  if (!includeHidden) {
    if (HIDDEN_CONFIGURE_PATHS.has(relativePath)) return null;
    // Detail paths under a hidden root are also hidden.
    for (const hidden of HIDDEN_CONFIGURE_PATHS) {
      if (relativePath.startsWith(hidden + '/')) return null;
    }

    // Per-provider integrations and plugin detail pages are hidden: the
    // single Integrations overview row is the entry point. Checked before
    // the prefix walk so they do not inherit Connect from a parent.
    if (
      relativePath.startsWith('/org/integrations/') ||
      relativePath.startsWith('/configure/connect/integrations/') ||
      relativePath.startsWith('/configure/connect/plugins/')
    )
      return null;
  }

  // Hidden pages (and their detail children) inherit a group when searched.
  if (includeHidden) {
    if (HIDDEN_CONFIGURE_PATHS.has(relativePath)) {
      const searchLabel = CONFIGURE_SEARCH_LABELS[relativePath];
      return {
        group: fallbackGroupForHiddenPath(relativePath, drillLabel),
        ...(searchLabel ? { label: searchLabel } : {}),
      };
    }
    for (const hidden of HIDDEN_CONFIGURE_PATHS) {
      if (relativePath.startsWith(hidden + '/')) {
        const searchLabel = CONFIGURE_SEARCH_LABELS[relativePath];
        return {
          group: fallbackGroupForHiddenPath(hidden, drillLabel),
          ...(searchLabel ? { label: searchLabel } : {}),
        };
      }
    }
    if (
      relativePath.startsWith('/org/integrations/') ||
      relativePath.startsWith('/configure/connect/integrations/') ||
      relativePath.startsWith('/configure/connect/plugins/')
    ) {
      return { group: 'Connect' };
    }
  }

  let bestKey: string | null = null;
  for (const key of Object.keys(CONFIGURE_SECTION_MAP)) {
    if (relativePath.startsWith(key + '/') && (bestKey === null || key.length > bestKey.length)) {
      bestKey = key;
    }
  }
  if (bestKey) {
    // Detail pages inherit the parent's group but keep their own label
    // from the source (no override).
    const parent = CONFIGURE_SECTION_MAP[bestKey]!;
    return { group: parent.group };
  }

  return { group: fallbackGroupForDrillLabel(drillLabel) };
}

/** Bucket a hidden path by its prefix when the palette wants to surface it. */
function fallbackGroupForHiddenPath(relativePath: string, drillLabel: string): ConfigureGroupLabel {
  if (
    relativePath.startsWith('/configure/connect/') ||
    relativePath.startsWith('/security/') ||
    relativePath.startsWith('/org/secrets') ||
    relativePath.startsWith('/org/model-providers') ||
    relativePath.startsWith('/org/plugins')
  ) {
    return 'Connect';
  }
  if (relativePath.startsWith('/configure/build/') || relativePath.startsWith('/agent-types')) {
    return 'Build';
  }
  if (relativePath.startsWith('/configure/organization')) {
    return 'Organization';
  }
  return fallbackGroupForDrillLabel(drillLabel);
}
